import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "@/components/waves/drops/identityDisplay.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { WavePodiumItemContentOutcomes } from "./WavePodiumItemContentOutcomes";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { motion } from "framer-motion";
import { WaveWinnersPodiumPlaceholder } from "./WaveWinnersPodiumPlaceholder";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
interface WavePodiumItemProps {
  readonly winner?: ApiWaveDecisionWinner | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly position: "first" | "second" | "third";
  readonly customAnimationIndex?: number | undefined;
}
interface PodiumAvatarProps {
  readonly label: string;
  readonly pfp: string | null | undefined;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
  readonly className: string;
  readonly ringClass: string;
  readonly ringWidthClass?: string | undefined;
  readonly shadowClass?: string | undefined;
  readonly fallbackTextClass?: string | undefined;
}

// Configuration for position-specific styling with CSS ready classes for Tailwind
const positionStyles = {
  first: {
    color: "#fbbf24",
    // amber-400 - Gold from WinnerDropBadge
    height: "tw-h-[220px]",
    pfpSize: "tw-size-12 md:tw-size-14",
    marginBottom: "-tw-mb-6",
    textColor: "tw-text-[#fbbf24]",
    ring: "tw-ring-[#fbbf24]",
    shadow: "tw-shadow-[0_0_20px_rgba(251,191,36,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base md:tw-text-xl",
    ratingFontSize: "tw-text-sm sm:tw-text-base md:tw-text-2xl",
    positionText: "1st",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#fbbf24]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#fbbf24]/5",
      via: "tw-via-[#fbbf24]/3",
      hover: "group-hover:desktop-hover:tw-from-[#fbbf24]/[0.07]",
      top: "tw-via-[#fbbf24]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#fbbf24]/25",
      sides: "tw-from-[#fbbf24]/10 tw-via-[#fbbf24]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#fbbf24]/25"
    }
  },
  second: {
    color: "#94a3b8",
    // slate-400 - Silver from WinnerDropBadge
    height: "tw-h-[190px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#94a3b8]",
    ring: "tw-ring-[#94a3b8]",
    shadow: "tw-shadow-[0_0_20px_rgba(148,163,184,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-sm sm:tw-text-base",
    positionText: "2nd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#94a3b8]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#94a3b8]/5",
      via: "tw-via-[#94a3b8]/3",
      hover: "group-hover:desktop-hover:tw-from-[#94a3b8]/[0.07]",
      top: "tw-via-[#94a3b8]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#94a3b8]/25",
      sides: "tw-from-[#94a3b8]/10 tw-via-[#94a3b8]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#94a3b8]/25"
    }
  },
  third: {
    color: "#CD7F32",
    // Bronze (same in both components)
    height: "tw-h-[170px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#CD7F32]",
    ring: "tw-ring-[#CD7F32]",
    shadow: "tw-shadow-[0_0_20px_rgba(205,127,50,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-base",
    positionText: "3rd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#CD7F32]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#CD7F32]/5",
      via: "tw-via-[#CD7F32]/3",
      hover: "group-hover:desktop-hover:tw-from-[#CD7F32]/[0.07]",
      top: "tw-via-[#CD7F32]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#CD7F32]/25",
      sides: "tw-from-[#CD7F32]/10 tw-via-[#CD7F32]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#CD7F32]/25"
    }
  }
};

// Animation variants for the podium items
const podiumVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.2, 0.9, 0.3, 1],
      opacity: {
        duration: 0.25
      }
    }
  })
};

// Height mapping for different positions
const heightMap = {
  first: "tw-h-[220px]",
  second: "tw-h-[190px]",
  third: "tw-h-[170px]"
};
const animationIndexMap: Record<WavePodiumItemProps["position"], number> = {
  first: 0,
  second: 1,
  third: 2
};
const getHoverTextColorClass = (position: WavePodiumItemProps["position"]) => {
  const colorMap = {
    first: "desktop-hover:hover:tw-text-[#fbbf24]",
    second: "desktop-hover:hover:tw-text-[#94a3b8]",
    third: "desktop-hover:hover:tw-text-[#CD7F32]"
  } as const;
  return colorMap[position];
};
const getAuthorProfileLabel = (drop: ExtendedDrop): string => drop.author.handle ?? drop.author.primary_address;
const getAuthorTooltipUser = (drop: ExtendedDrop): string => drop.author.handle ?? drop.author.primary_address;
const getIdentityHref = (value: string) => `/${encodeURIComponent(value.toLowerCase())}`;
const getPodiumIdentityDisplay = (drop: ExtendedDrop) => {
  const identityProfile = getDropIdentityProfile({
    wave: drop.wave,
    metadata: drop.metadata
  });
  const fallbackValue = identityProfile ? null : getDropIdentityFallbackValue({
    wave: drop.wave,
    metadata: drop.metadata
  });
  const label = identityProfile?.handle ?? identityProfile?.primary_address ?? fallbackValue;
  if (!label) {
    return null;
  }
  return {
    label,
    pfp: identityProfile?.pfp ?? null,
    profileUser: identityProfile?.handle ?? identityProfile?.primary_address ?? null,
    comparableIdentity: identityProfile ?? {
      handle: fallbackValue,
      primary_address: fallbackValue
    }
  };
};
const PodiumAvatar: React.FC<PodiumAvatarProps> = t0 => {
  const $ = _c(16);
  const {
    label,
    pfp,
    alt,
    width,
    height,
    className,
    ringClass,
    ringWidthClass: t1,
    shadowClass,
    fallbackTextClass: t2
  } = t0;
  const ringWidthClass = t1 === undefined ? "tw-ring-2" : t1;
  const fallbackTextClass = t2 === undefined ? "tw-text-xs tw-font-semibold tw-text-iron-100" : t2;
  let t3;
  if ($[0] !== label) {
    t3 = label.trim().charAt(0).toUpperCase() || "?";
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const initial = t3;
  if (pfp) {
    let t4;
    if ($[2] !== pfp) {
      t4 = getScaledImageUri(pfp, ImageScale.W_AUTO_H_50);
      $[2] = pfp;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    const t5 = `${className} ${ringWidthClass} ${ringClass} tw-object-cover ${shadowClass ?? ""}`;
    let t6;
    if ($[4] !== alt || $[5] !== height || $[6] !== t4 || $[7] !== t5 || $[8] !== width) {
      t6 = <Image src={t4} alt={alt} width={width} height={height} className={t5} />;
      $[4] = alt;
      $[5] = height;
      $[6] = t4;
      $[7] = t5;
      $[8] = width;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const t4 = `${className} ${ringWidthClass} ${ringClass} ${shadowClass ?? ""} tw-flex tw-items-center tw-justify-center tw-bg-iron-900`;
  let t5;
  if ($[10] !== fallbackTextClass || $[11] !== initial) {
    t5 = <span className={fallbackTextClass}>{initial}</span>;
    $[10] = fallbackTextClass;
    $[11] = initial;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div aria-hidden="true" className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const WavePodiumItem: React.FC<WavePodiumItemProps> = t0 => {
  const $ = _c(128);
  const {
    winner,
    onDropClick,
    position,
    customAnimationIndex
  } = t0;
  const styles = positionStyles[position];
  let t1;
  if ($[0] !== position) {
    t1 = getHoverTextColorClass(position);
    $[0] = position;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hoverTextColorClass = t1;
  if (!winner) {
    const t2 = heightMap[position];
    let t3;
    if ($[2] !== position || $[3] !== t2) {
      t3 = <WaveWinnersPodiumPlaceholder height={t2} position={position} />;
      $[2] = position;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  const drop = winner.drop as ExtendedDrop;
  const animationIndex = customAnimationIndex ?? animationIndexMap[position];
  let t2;
  if ($[5] !== drop) {
    t2 = getAuthorProfileLabel(drop);
    $[5] = drop;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const authorProfileLabel = t2;
  let t3;
  if ($[7] !== drop) {
    t3 = getAuthorTooltipUser(drop);
    $[7] = drop;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const authorTooltipUser = t3;
  const authorProfileHref = `/${authorProfileLabel}`;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== animationIndex || $[10] !== authorProfileHref || $[11] !== authorProfileLabel || $[12] !== authorTooltipUser || $[13] !== drop || $[14] !== hoverTextColorClass || $[15] !== onDropClick || $[16] !== styles.autorFontSize || $[17] !== styles.bgGradient || $[18] !== styles.gradient.from || $[19] !== styles.gradient.hover || $[20] !== styles.gradient.hover25 || $[21] !== styles.gradient.sides || $[22] !== styles.gradient.sidesHover || $[23] !== styles.gradient.top || $[24] !== styles.gradient.via || $[25] !== styles.height || $[26] !== styles.marginBottom || $[27] !== styles.pfpSize || $[28] !== styles.positionText || $[29] !== styles.ring || $[30] !== styles.shadow || $[31] !== styles.textColor) {
    const identityDisplay = getPodiumIdentityDisplay(drop);
    const primaryLabel = identityDisplay?.label ?? authorProfileLabel;
    const primaryPfp = identityDisplay ? identityDisplay.pfp : drop.author.pfp;
    const isSelfNominated = identityDisplay ? areSameProfileIdentity({
      left: drop.author,
      right: identityDisplay.comparableIdentity
    }) : false;
    T0 = motion.div;
    t12 = podiumVariants;
    t13 = "hidden";
    t14 = "visible";
    t15 = animationIndex;
    if ($[45] !== drop || $[46] !== onDropClick) {
      t10 = () => onDropClick(drop);
      $[45] = drop;
      $[46] = onDropClick;
      $[47] = t10;
    } else {
      t10 = $[47];
    }
    t11 = "tw-group tw-cursor-pointer";
    t8 = "tw-flex tw-flex-col tw-items-center";
    const t16 = `tw-flex tw-flex-col tw-items-center ${styles.marginBottom} tw-relative tw-z-10`;
    const t17 = `tw-absolute tw-inset-0 ${styles.bgGradient}`;
    let t18;
    if ($[48] !== t17) {
      t18 = <div className={t17} />;
      $[48] = t17;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    const t19 = identityDisplay ? <div className="tw-transform tw-transition-transform tw-duration-300 group-hover:desktop-hover:tw-scale-[1.02]"><PodiumAvatar label={primaryLabel} pfp={primaryPfp} alt={`${primaryLabel} avatar`} width={56} height={56} className={`${styles.pfpSize} tw-rounded-xl`} ringClass={styles.ring} shadowClass={styles.shadow} /></div> : <Link href={authorProfileHref} onClick={_temp} className="tw-transform tw-transition-all tw-duration-300 hover:tw-scale-105"><PodiumAvatar label={authorProfileLabel} pfp={drop.author.pfp} alt={`${authorProfileLabel} avatar`} width={56} height={56} className={`${styles.pfpSize} tw-rounded-xl`} ringClass={styles.ring} shadowClass={styles.shadow} /></Link>;
    const t20 = `tw-w-3 md:tw-size-4 ${styles.textColor}`;
    let t21;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" />;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    let t22;
    if ($[51] !== t20) {
      t22 = <svg className={t20} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">{t21}</svg>;
      $[51] = t20;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    const t23 = `${styles.textColor} tw-text-xs tw-font-medium lg:tw-text-sm`;
    let t24;
    if ($[53] !== styles.positionText || $[54] !== t23) {
      t24 = <span className={t23}>{styles.positionText}</span>;
      $[53] = styles.positionText;
      $[54] = t23;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    let t25;
    if ($[56] !== t22 || $[57] !== t24) {
      t25 = <div className="tw-absolute tw-inset-x-0 -tw-bottom-3 tw-flex tw-justify-center"><div className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-iron-700 tw-bg-iron-900/80 tw-px-3 tw-py-1 tw-shadow-lg tw-backdrop-blur-sm">{t22}{t24}</div></div>;
      $[56] = t22;
      $[57] = t24;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    if ($[59] !== t16 || $[60] !== t18 || $[61] !== t19 || $[62] !== t25) {
      t9 = <div className={t16}>{t18}{t19}{t25}</div>;
      $[59] = t16;
      $[60] = t18;
      $[61] = t19;
      $[62] = t25;
      $[63] = t9;
    } else {
      t9 = $[63];
    }
    t7 = "tw-relative tw-w-full";
    t4 = `${styles.height} tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-iron-800/60 tw-bg-gradient-to-b tw-from-iron-900/70 tw-via-iron-900/50 tw-to-transparent tw-shadow-[0_4px_24px_rgba(0,0,0,0.2)] tw-backdrop-blur-xl tw-transition-all tw-duration-300 tw-ease-out group-hover:desktop-hover:tw-border-iron-700/70 group-hover:desktop-hover:tw-from-iron-900/75 group-hover:desktop-hover:tw-via-iron-900/55 group-hover:desktop-hover:tw-to-transparent group-hover:desktop-hover:tw-shadow-[0_0_48px_rgba(0,0,0,0.35)]`;
    const t26 = `tw-absolute tw-inset-0 tw-bg-gradient-to-b ${styles.gradient.from} ${styles.gradient.via} tw-to-transparent ${styles.gradient.hover}`;
    let t27;
    if ($[64] !== t26) {
      t27 = <div className={t26} />;
      $[64] = t26;
      $[65] = t27;
    } else {
      t27 = $[65];
    }
    const t28 = `tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent ${styles.gradient.top} tw-to-transparent ${styles.gradient.hover25}`;
    let t29;
    if ($[66] !== t28) {
      t29 = <div className={t28} />;
      $[66] = t28;
      $[67] = t29;
    } else {
      t29 = $[67];
    }
    const t30 = `tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
    let t31;
    if ($[68] !== t30) {
      t31 = <div className={t30} />;
      $[68] = t30;
      $[69] = t31;
    } else {
      t31 = $[69];
    }
    const t32 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
    let t33;
    if ($[70] !== t32) {
      t33 = <div className={t32} />;
      $[70] = t32;
      $[71] = t33;
    } else {
      t33 = $[71];
    }
    let t34;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-3/4 tw-bg-gradient-to-t tw-from-black/20 tw-via-black/10 tw-to-transparent" />;
      $[72] = t34;
    } else {
      t34 = $[72];
    }
    if ($[73] !== t27 || $[74] !== t29 || $[75] !== t31 || $[76] !== t33) {
      t5 = <div className="tw-absolute tw-inset-0">{t27}{t29}{t31}{t33}{t34}</div>;
      $[73] = t27;
      $[74] = t29;
      $[75] = t31;
      $[76] = t33;
      $[77] = t5;
    } else {
      t5 = $[77];
    }
    t6 = identityDisplay ? <div className="tw-mb-2 tw-mt-2 tw-flex tw-max-w-full tw-flex-col tw-items-center tw-gap-y-1 tw-px-3 sm:tw-mt-3">{identityDisplay.profileUser ? <UserProfileTooltipWrapper user={identityDisplay.profileUser}><Link href={getIdentityHref(identityDisplay.profileUser)} onClick={_temp2} className={`tw-relative tw-block tw-max-w-full tw-text-center tw-no-underline tw-transition-all ${hoverTextColorClass} tw-group/link`}><span title={primaryLabel} className={`${styles.autorFontSize} tw-block tw-max-w-full tw-truncate tw-font-semibold tw-text-iron-100 ${hoverTextColorClass} tw-transition-colors`}>{primaryLabel}</span><svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-absolute tw-left-[100%] tw-top-1/2 tw-ml-2 -tw-translate-y-1/2 desktop-hover:group-hover/link:tw-opacity-100`}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg></Link></UserProfileTooltipWrapper> : <span title={primaryLabel} className={`${styles.autorFontSize} tw-block tw-max-w-full tw-truncate tw-text-center tw-font-semibold tw-text-iron-100`}>{primaryLabel}</span>}<div className="tw-flex tw-max-w-full tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1 tw-gap-y-1 tw-text-[11px] tw-text-iron-500">{isSelfNominated ? <span className="tw-font-normal tw-text-iron-500">self-nominated</span> : <><span className="tw-font-normal tw-text-iron-500">nominated by</span><UserProfileTooltipWrapper user={authorTooltipUser}><Link href={authorProfileHref} onClick={_temp3} className="tw-inline-flex tw-max-w-full tw-items-center tw-text-iron-400 tw-no-underline tw-transition-colors desktop-hover:hover:tw-text-iron-200"><span title={authorProfileLabel} className="tw-block tw-max-w-[7rem] tw-truncate tw-text-[11px] tw-font-medium tw-text-iron-400 tw-transition-colors desktop-hover:hover:tw-text-iron-200">{authorProfileLabel}</span></Link></UserProfileTooltipWrapper></>}</div></div> : <UserProfileTooltipWrapper user={authorTooltipUser}><Link href={authorProfileHref} onClick={_temp4} className={`tw-relative tw-mb-2 tw-mt-2 tw-text-center tw-no-underline tw-transition-all sm:tw-mt-4 ${hoverTextColorClass} tw-group/link`}><span className={`${styles.autorFontSize} tw-font-semibold tw-text-iron-200 ${hoverTextColorClass} tw-inline-flex tw-items-center tw-transition-colors`}>{authorProfileLabel}<svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-absolute tw-left-[100%] tw-ml-2 desktop-hover:group-hover/link:tw-opacity-100`}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg></span></Link></UserProfileTooltipWrapper>;
    $[9] = animationIndex;
    $[10] = authorProfileHref;
    $[11] = authorProfileLabel;
    $[12] = authorTooltipUser;
    $[13] = drop;
    $[14] = hoverTextColorClass;
    $[15] = onDropClick;
    $[16] = styles.autorFontSize;
    $[17] = styles.bgGradient;
    $[18] = styles.gradient.from;
    $[19] = styles.gradient.hover;
    $[20] = styles.gradient.hover25;
    $[21] = styles.gradient.sides;
    $[22] = styles.gradient.sidesHover;
    $[23] = styles.gradient.top;
    $[24] = styles.gradient.via;
    $[25] = styles.height;
    $[26] = styles.marginBottom;
    $[27] = styles.pfpSize;
    $[28] = styles.positionText;
    $[29] = styles.ring;
    $[30] = styles.shadow;
    $[31] = styles.textColor;
    $[32] = T0;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    T0 = $[32];
    t10 = $[33];
    t11 = $[34];
    t12 = $[35];
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
    t4 = $[39];
    t5 = $[40];
    t6 = $[41];
    t7 = $[42];
    t8 = $[43];
    t9 = $[44];
  }
  const t16 = `${drop.rating >= 0 ? styles.textColor : "tw-text-[#ff4466]"} tw-font-semibold ${styles.ratingFontSize}`;
  let t17;
  if ($[78] !== drop.rating) {
    t17 = formatNumberWithCommas(drop.rating);
    $[78] = drop.rating;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  let t18;
  if ($[80] !== t16 || $[81] !== t17) {
    t18 = <span className={t16}>{t17}</span>;
    $[80] = t16;
    $[81] = t17;
    $[82] = t18;
  } else {
    t18 = $[82];
  }
  const t19 = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  let t20;
  if ($[83] !== t19) {
    t20 = <span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{t19}</span>;
    $[83] = t19;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  let t21;
  if ($[85] !== t18 || $[86] !== t20) {
    t21 = <div className="tw-flex tw-items-center tw-gap-x-1">{t18}{t20}</div>;
    $[85] = t18;
    $[86] = t20;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  let t22;
  if ($[88] !== drop.raters_count) {
    t22 = formatNumberWithCommas(drop.raters_count);
    $[88] = drop.raters_count;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  let t23;
  if ($[90] !== t22) {
    t23 = <span className="tw-text-xs tw-text-iron-200 sm:tw-text-sm">{t22}</span>;
    $[90] = t22;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  const t24 = drop.raters_count === 1 ? "voter" : "voters";
  let t25;
  if ($[92] !== t24) {
    t25 = <span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{t24}</span>;
    $[92] = t24;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== t23 || $[95] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-gap-x-1">{t23}{t25}</div>;
    $[94] = t23;
    $[95] = t25;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  let t27;
  if ($[97] !== winner) {
    t27 = <div onClick={_temp5}><WavePodiumItemContentOutcomes winner={winner} /></div>;
    $[97] = winner;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  let t28;
  if ($[99] !== t26 || $[100] !== t27) {
    t28 = <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t26}{t27}</div>;
    $[99] = t26;
    $[100] = t27;
    $[101] = t28;
  } else {
    t28 = $[101];
  }
  let t29;
  if ($[102] !== t21 || $[103] !== t28) {
    t29 = <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t21}{t28}</div>;
    $[102] = t21;
    $[103] = t28;
    $[104] = t29;
  } else {
    t29 = $[104];
  }
  let t30;
  if ($[105] !== t29 || $[106] !== t4 || $[107] !== t5 || $[108] !== t6) {
    t30 = <div className={t4}>{t5}{t6}{t29}</div>;
    $[105] = t29;
    $[106] = t4;
    $[107] = t5;
    $[108] = t6;
    $[109] = t30;
  } else {
    t30 = $[109];
  }
  let t31;
  if ($[110] !== t30 || $[111] !== t7) {
    t31 = <div className={t7}>{t30}</div>;
    $[110] = t30;
    $[111] = t7;
    $[112] = t31;
  } else {
    t31 = $[112];
  }
  let t32;
  if ($[113] !== t31 || $[114] !== t8 || $[115] !== t9) {
    t32 = <div className={t8}>{t9}{t31}</div>;
    $[113] = t31;
    $[114] = t8;
    $[115] = t9;
    $[116] = t32;
  } else {
    t32 = $[116];
  }
  let t33;
  if ($[117] !== t10 || $[118] !== t11 || $[119] !== t32) {
    t33 = <div onClick={t10} className={t11}>{t32}</div>;
    $[117] = t10;
    $[118] = t11;
    $[119] = t32;
    $[120] = t33;
  } else {
    t33 = $[120];
  }
  let t34;
  if ($[121] !== T0 || $[122] !== t12 || $[123] !== t13 || $[124] !== t14 || $[125] !== t15 || $[126] !== t33) {
    t34 = <T0 variants={t12} initial={t13} animate={t14} custom={t15}>{t33}</T0>;
    $[121] = T0;
    $[122] = t12;
    $[123] = t13;
    $[124] = t14;
    $[125] = t15;
    $[126] = t33;
    $[127] = t34;
  } else {
    t34 = $[127];
  }
  return t34;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_2) {
  return e_2.stopPropagation();
}
function _temp5(e_3) {
  return e_3.stopPropagation();
}